/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { appendObjectValuesToFormData } from '@veone/ngx-utilities/form';
import { Observable, map, share } from 'rxjs';
import {
  TvShow,
  TvShowLive,
  TvShowListParams,
  Pagination,
  TvShowCreationParams,
  Metrics
} from '~core/models/core.models';
import { ApiConstantsService } from './api-constants.service';

@Injectable()
export class TvShowService {
  protected _httpClient = inject(HttpClient);
  protected _apiConstantsService = inject(ApiConstantsService);

  read(tvShowId: string): Observable<TvShow> {
    return this._httpClient.get<TvShow>(
      `${this._apiConstantsService.tvShowUrl}/${tvShowId}`
    );
  }

  readLive(): Observable<TvShowLive> {
    return this._httpClient.get<TvShowLive>(
      `${this._apiConstantsService.tvShowUrl}/live`
    );
  }

  list(params?: TvShowListParams): Observable<Pagination<TvShow>> {
    return this._httpClient
      .get<Pagination<TvShow>>(this._apiConstantsService.tvShowUrl, {
        params: {
          ...params,
          sort: params?.sort || 'createdAt',
          desc: params?.desc || 'createdAt'
        } as Record<string, any>
      })
      .pipe(
        map(res => {
          if (res.items.length) {
            res.items = res.items.map(el => ({
              ...el,
              videoCountMetrics$: this.getVideoCountMetrics(el.id)
            }));
          }
          return res;
        })
      );
  }

  create(data: TvShowCreationParams): Observable<TvShow> {
    const formData = new FormData();
    appendObjectValuesToFormData(formData, data);

    return this._httpClient.post<TvShow>(
      this._apiConstantsService.tvShowUrl,
      formData
    );
  }

  update(
    tvShowId: string,
    data: Partial<TvShowCreationParams>
  ): Observable<TvShow> {
    let body: Record<string, unknown> | FormData = data;
    if ('logo' in data || 'thumbnail' in data) {
      const formData = new FormData();
      appendObjectValuesToFormData(formData, data);
      body = formData;
    }

    return this._httpClient.post<TvShow>(
      `${this._apiConstantsService.tvShowUrl}/${tvShowId}`,
      body
    );
  }

  delete(tvShowId: string): Observable<unknown> {
    return this._httpClient.delete(
      `${this._apiConstantsService.tvShowUrl}/${tvShowId}`
    );
  }

  getVideoCountMetrics(
    tvShowId: string
  ): Observable<Pick<Metrics, 'playsCount'>> {
    return this._httpClient
      .get<
        Pick<Metrics, 'playsCount'>
      >(`${this._apiConstantsService.tvShowUrl}/${tvShowId}/kaltura-video-count-plays`)
      .pipe(share());
  }

  deleteBadges(tvShowId: string, badgeIds: string[]): Observable<unknown> {
    return this._httpClient.delete<unknown>(
      `${this._apiConstantsService.tvShowUrl}/${tvShowId}/badges`,
      {
        body: {
          badges: badgeIds
        }
      }
    );
  }
}
